import Card from 'components/UI/Card';
import CardHeader from 'components/UI/Card/CardHeader';
import { ConnectivityDetails, DeviceFeatureFilterAttribute } from 'models';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from 'components/UI/Button';
import { downloadFile } from 'utils/exporting';
import { showErrorToast } from 'utils/toast';
import { getDeviceSpecification } from 'services/connectivityService/connectivityService';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import SelectField, { OptionType } from 'components/UI/SelectField';
import { TwoColumnGrid } from 'styles';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  connectivityDetailsSelector,
  fetchDeviceShadow,
  resetDesiredState,
} from 'store/connectivityState/connectivityState';
import ShadowTree from '../ShadowTree';
import Loader from 'components/UI/Loader';
import { DownloadButtonGrid } from './styled';
import ViewDesiredModal from '../ViewDesiredModal';

type Props = {
  featureOptionTypes: OptionType[];
  id: string;
  defaultFeature: string | null;
};

const DeviceShadowCard: FC<Props> = ({
  id,
  featureOptionTypes,
  defaultFeature,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [openDesiredModal, setOpenDesiredModal] = useState<boolean>(false);
  const { deviceShadow, desired }: ConnectivityDetails = useAppSelector(
    connectivityDetailsSelector,
  );

  const {
    setValue,
    register,
    watch,
    formState: { errors },
  } = useForm();

  const [
    selectedFeatureKey,
    selectedFeatureValue,
    selectedFeatureSpecification,
  ] = watch([
    DeviceFeatureFilterAttribute.FeatureKey,
    DeviceFeatureFilterAttribute.FeatureValue,
    DeviceFeatureFilterAttribute.FeatureSpecification,
  ]);

  // Fetch selected feature
  useEffect(() => {
    if (id && selectedFeatureKey && !deviceShadow.error) {
      dispatch(
        fetchDeviceShadow(id, selectedFeatureKey, selectedFeatureSpecification),
      );
    }
    // First time we set the default feature to set the selectfield value and fetch correct shadow
    if (id && !selectedFeatureKey && defaultFeature) {
      setValue(DeviceFeatureFilterAttribute.FeatureKey, defaultFeature);
      const selectedOption =
        featureOptionTypes.find((option) => option.key === defaultFeature) ||
        featureOptionTypes[0];
      setValue(
        DeviceFeatureFilterAttribute.FeatureValue,
        selectedOption?.value,
      );
      setValue(
        DeviceFeatureFilterAttribute.FeatureSpecification,
        selectedOption?.spec,
      );
    }
    // First time we set first item to default if defaultFeature is not set
    if (id && !selectedFeatureKey && !defaultFeature) {
      setValue(
        DeviceFeatureFilterAttribute.FeatureKey,
        featureOptionTypes[0].key,
      );
      setValue(
        DeviceFeatureFilterAttribute.FeatureValue,
        featureOptionTypes[0].value,
      );
      setValue(
        DeviceFeatureFilterAttribute.FeatureSpecification,
        featureOptionTypes[0].spec,
      );
    }
  }, [
    dispatch,
    selectedFeatureKey,
    id,
    defaultFeature,
    setValue,
    featureOptionTypes,
    selectedFeatureSpecification,
    deviceShadow.error,
    desired.error,
    intl,
  ]);

  useEffect(() => {
    if (deviceShadow?.error) {
      showErrorToast(
        `${intl.formatMessage(
          {
            id: 'connectivity.device_shadow_error',
          },
          { error: deviceShadow?.error?.title },
        )}.`,
      );
    }
  }, [deviceShadow?.error, intl]);

  useEffect(() => {
    if (desired?.error) {
      showErrorToast(
        `${intl.formatMessage(
          {
            id: 'device_desired_values.error',
          },
          { error: desired?.error?.title },
        )}.`,
      );
    }
    dispatch(resetDesiredState());
  }, [desired?.error, dispatch, intl]);

  // Download JSON file
  const onExportShadow = useCallback(() => {
    if (deviceShadow.exportData) {
      downloadFile(
        deviceShadow.exportData,
        'device_shadow',
        'application/json',
        'json',
      );
    }
  }, [deviceShadow.exportData]);

  // Download JSON file
  const onExportSpecification = useCallback(
    async (specId: string) => {
      if (specId) {
        try {
          const res = await getDeviceSpecification(specId);
          if (res.data?.attributes) {
            const jsonString = JSON.stringify(res.data.attributes);
            downloadFile(
              jsonString,
              `${specId}_specification.json`,
              'application/json',
              'json',
            );
          }
        } catch (error) {
          console.error('Error exporting specification', error);
          showErrorToast(
            `${intl.formatMessage({ id: 'file_download.error' })}.`,
          );
        }
      }
    },
    [intl],
  );

  const onDesiredModalClose = useCallback(
    () => setOpenDesiredModal(false),
    [setOpenDesiredModal],
  );

  const onSelect = useCallback(
    (name: string) => (value: string) => {
      const featureKey = featureOptionTypes.find(
        (option) => option.value === value,
      );
      setValue(name, featureKey?.value);
      setValue(DeviceFeatureFilterAttribute.FeatureKey, featureKey?.key);
      setValue(
        DeviceFeatureFilterAttribute.FeatureSpecification,
        featureKey?.spec,
      );
    },
    [setValue, featureOptionTypes],
  );

  const onViewDesired = useCallback(
    () => setOpenDesiredModal(true),
    [setOpenDesiredModal],
  );

  const enableShowDesiredValues =
    selectedFeatureValue === 'happ-device' && (desired?.data?.length ?? 0) > 0;

  const shadowResult = useMemo(() => {
    if (deviceShadow.isLoading) {
      return <Loader center />;
    }
    return (
      <ShadowTree
        shadows={deviceShadow.data ?? []}
        selectedFeatureSpecification={selectedFeatureSpecification}
      />
    );
  }, [deviceShadow, selectedFeatureSpecification]);

  return (
    <Card>
      <CardHeader />
      <Heading type={HeadingType.h3} uppercase>
        <FormattedMessage id="connectivity.device_shadow" />
      </Heading>
      <TwoColumnGrid>
        <SelectField
          name={DeviceFeatureFilterAttribute.FeatureValue}
          error={errors.featureValue}
          onSelect={onSelect(DeviceFeatureFilterAttribute.FeatureValue)}
          register={register(DeviceFeatureFilterAttribute.FeatureValue)}
          label={intl.formatMessage({ id: 'connectivity.features' })}
          defaultValue={selectedFeatureValue}
          options={featureOptionTypes}
        />
        <DownloadButtonGrid>
          <Button
            id="export_shadow_btn"
            backgroundColor="transparent"
            color="primary"
            onClick={onExportShadow}
          >
            <FormattedMessage id="connectivity.export_data_btn" />
          </Button>
          <Button
            id="export_spec_btn"
            backgroundColor="transparent"
            color="primary"
            onClick={() => onExportSpecification(selectedFeatureSpecification)}
          >
            <FormattedMessage id="connectivity.export_spec_btn" />
          </Button>
          <Button
            backgroundColor="transparent"
            color="primary"
            onClick={() => navigator.clipboard.writeText(selectedFeatureSpecification)}
          >
            <FormattedMessage id="connectivity.copy_spec_id_btn" />
          </Button>
          {enableShowDesiredValues && (
            <Button
              id="view_desired_btn"
              backgroundColor="transparent"
              color="primary"
              onClick={() => onViewDesired()}
            >
              <FormattedMessage id="connectivity.view_desired_btn" />
            </Button>
          )}
          {desired?.data && (
            <ViewDesiredModal
              open={openDesiredModal}
              onClose={onDesiredModalClose}
              desiredData={desired.data}
            />
          )}
        </DownloadButtonGrid>
      </TwoColumnGrid>
      {shadowResult}
    </Card>
  );
};
export default DeviceShadowCard;
